// "Export workbook as linked .xlsx" — a workbook holding just the Areas
// system sheet plus every custom (user-created) sheet, so the GI.* formulas
// that actually do the work live alongside the one source table they read
// from, with the `GI.*` LAMBDA set (giLambdas.ts) spliced in so a fresh
// `=GI.SUM(...)` formula can be typed directly against the exported snapshot
// in real Excel. Features, Blocks, Usages and Analytics are left out —
// Areas alone already carries everything a GI.* formula groups or filters
// by (see areasTable.ts). Downloaded alongside a companion JSON file
// carrying the same tables, meant for a future Power Query setup to read —
// see giLambdas.ts's own header for exactly what this does and does not
// include yet (no Power Query, no Excel Table objects, nothing verified
// against real Excel).
//
// Reuses `buildXlsxParts` entirely for the sheet XML — a "linked" export is
// just an ordinary export of a workbook that happens to contain only Areas
// and the custom sheets, with LAMBDA defined names spliced into the
// workbook.xml it already produces.

import type { Sheet, Workbook } from '@/model/types';
import type { ExportReport, ExportSource } from './xlsxExport';
import { buildXlsxParts, toExcelFormula } from './xlsxExport';
import { writeZip } from './zipWrite';
import type { Bytes } from './zip';
import { GI_LAMBDAS } from './giLambdas';
import { colToLabel } from '@/model/address';

function linkedSubWorkbook(wb: Workbook): Workbook {
  const areas = wb.sheets.find((s) => s.role === 'areas');
  const custom = wb.sheets.filter((s) => s.role === undefined);
  const sheets = areas ? [areas, ...custom] : custom;
  return { sheets, activeSheetId: sheets[0]?.id ?? '', blocks: {}, charts: {} };
}

function escapeXmlText(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function escapeXmlAttr(s: string): string {
  return escapeXmlText(s).replace(/"/g, '&quot;');
}

/**
 * Splice the `GI.*` LAMBDA defined names into an already-built `workbook.xml`
 * — appended to its `<definedNames>` block if `buildXlsxParts` emitted one
 * (live-block named ranges), or a fresh one inserted before `<calcPr>` if not.
 *
 * Every formula goes through `toExcelFormula` first — the SAME future-function
 * prefixing an ordinary cell formula gets (`xlsxExport.ts`). Every one of
 * these LAMBDAs opens with a bare `LAMBDA(` and several call `LET`/`SEQUENCE`/
 * `TEXTSPLIT`/`TEXTJOIN`/`ISOMITTED`/`UNIQUE` — all future functions — and an
 * unprefixed future function inside a `<definedName>` does not surface as a
 * cell-level `#NAME?`, the way it would in a formula: Excel fails to parse the
 * whole defined name and reports the FILE as corrupted on open. This was that
 * bug — confirmed live by a real downloaded file real Excel refused to open.
 */
function withGiLambdas(workbookXml: string): string {
  const extra = GI_LAMBDAS.map(
    (d) => `<definedName name="${escapeXmlAttr(d.name)}">${escapeXmlText(toExcelFormula(d.formula))}</definedName>`
  ).join('');
  if (workbookXml.includes('</definedNames>')) {
    return workbookXml.replace('</definedNames>', `${extra}</definedNames>`);
  }
  return workbookXml.replace('<calcPr', `<definedNames>${extra}</definedNames><calcPr`);
}

export interface LinkedXlsxResult {
  parts: Record<string, string>;
  report: ExportReport;
  /** Areas (if present) plus every custom sheet actually included. */
  sheets: string[];
}

export function buildLinkedXlsxParts(wb: Workbook, source: ExportSource): LinkedXlsxResult {
  const sub = linkedSubWorkbook(wb);
  if (sub.sheets.length === 0) {
    throw new Error('Nothing to link yet — this workbook has no Areas sheet and no custom sheet of its own.');
  }
  const { parts, report } = buildXlsxParts(sub, { valueAt: source.valueAt, currency: source.currency });
  parts['xl/workbook.xml'] = withGiLambdas(parts['xl/workbook.xml']!);
  return { parts, report, sheets: sub.sheets.map((s) => s.name) };
}

export async function exportLinkedXlsx(wb: Workbook, source: ExportSource): Promise<{ bytes: Bytes; report: ExportReport; sheets: string[] }> {
  const { parts, report, sheets } = buildLinkedXlsxParts(wb, source);
  return { bytes: await writeZip(parts), report, sheets };
}

// ---------------------------------------------------------------------------
// The companion JSON — the same Areas-plus-custom tables, as plain row
// objects keyed by header text, for a Power Query setup (not built here —
// see giLambdas.ts) to read later.
// ---------------------------------------------------------------------------

export interface LinkedJsonTable {
  sheet: string;
  rows: Record<string, unknown>[];
}

export interface LinkedJsonExport {
  exportedAt: string;
  tables: LinkedJsonTable[];
}

/** Every filled cell on `sheet`'s header row (1), by column. */
function headerRow(sheet: Sheet, source: ExportSource): Map<number, string> {
  const out = new Map<number, string>();
  for (let c = 1; c <= sheet.cols; c++) {
    const v = source.valueAt(sheet, 1, c);
    if (v !== undefined && v !== null && String(v).trim() !== '') out.set(c, String(v));
  }
  return out;
}

function tableFor(sheet: Sheet, source: ExportSource): LinkedJsonTable {
  const headers = headerRow(sheet, source);
  const rows: Record<string, unknown>[] = [];
  for (let r = 2; r <= sheet.rows; r++) {
    const row: Record<string, unknown> = {};
    let any = false;
    for (const [c, header] of headers) {
      const v = source.valueAt(sheet, r, c);
      if (v === undefined || v === null || v === '') continue;
      row[header] = v;
      any = true;
    }
    if (any) rows.push(row);
  }
  return { sheet: sheet.name, rows };
}

/** Builds the JSON export's tables, given the same workbook `exportLinkedXlsx` just wrote. */
export function buildLinkedJson(wb: Workbook, source: ExportSource, now: string): LinkedJsonExport {
  const sub = linkedSubWorkbook(wb);
  return { exportedAt: now, tables: sub.sheets.map((s) => tableFor(s, source)) };
}

/** `Features!$A$1:$P$50`-style whole-table reference, for pasting into a `GI.*` call by hand. */
export function wholeTableRef(sheet: Sheet): string {
  return `${sheet.name}!$A$1:$${colToLabel(sheet.cols)}$${sheet.rows}`;
}
